import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Switch,
  FormControlLabel,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import api from '../../utils/api';
import { logger } from '../../utils/logger';
import {
  FEATURE_FLAG_REGISTRY,
  type FeatureFlags,
  type FeatureFlagMetadata,
  featureFlags as featureFlagService,
} from '../../utils/featureFlags';

interface FeatureFlagsResponse {
  flags: Partial<FeatureFlags>;
  updatedAt: string | null;
  updatedBy: string | null;
}

/**
 * Initial flag state for the form, seeded from registry defaults so the UI
 * has sensible values even while the initial GET is in flight.
 */
const initialFlagsFromRegistry = (): Record<string, boolean> => {
  return FEATURE_FLAG_REGISTRY.reduce<Record<string, boolean>>((acc, entry) => {
    acc[entry.key] = entry.defaultValue;
    return acc;
  }, {});
};

/**
 * Group registry entries by category so the UI can render section headers.
 */
const groupByCategory = (
  registry: FeatureFlagMetadata[],
): Array<{ category: string; entries: FeatureFlagMetadata[] }> => {
  const map = new Map<string, FeatureFlagMetadata[]>();
  for (const entry of registry) {
    const key = entry.category ?? 'General';
    const existing = map.get(key) ?? [];
    existing.push(entry);
    map.set(key, existing);
  }
  return Array.from(map.entries()).map(([category, entries]) => ({
    category,
    entries,
  }));
};

const FeatureFlagsTab: React.FC = () => {
  const [flags, setFlags] = useState<Record<string, boolean>>(initialFlagsFromRegistry);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const response = await api.get<FeatureFlagsResponse>('/api/admin/feature-flags');
        const data = response.data;
        setFlags((prev) => ({ ...prev, ...data.flags }));
        setUpdatedAt(data.updatedAt);
        setUpdatedBy(data.updatedBy);
      } catch (err) {
        logger.error('Failed to load feature flags config', err);
        setErrorMessage('Failed to load feature flags.');
      } finally {
        setLoading(false);
      }
    };

    fetchFlags();
  }, []);

  const handleToggle = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.checked;
    setFlags((prev) => ({ ...prev, [key]: next }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      // Only send flag keys that are in the registry — guards against stale
      // state in the form containing keys the backend no longer knows about.
      const registryKeys = new Set(FEATURE_FLAG_REGISTRY.map((e) => e.key));
      const payload: Record<string, boolean> = {};
      for (const [key, value] of Object.entries(flags)) {
        if (registryKeys.has(key as keyof FeatureFlags)) {
          payload[key] = Boolean(value);
        }
      }

      const response = await api.put<FeatureFlagsResponse>(
        '/api/admin/feature-flags',
        { flags: payload },
      );
      const data = response.data;

      setFlags((prev) => ({ ...prev, ...data.flags }));
      setUpdatedAt(data.updatedAt);
      setUpdatedBy(data.updatedBy);
      setSuccessMessage('Feature flags saved successfully.');

      // Update the in-memory singleton so any NEW component mounts in this
      // session pick up the change. Components already mounted will not
      // re-render — they see the change on next reload. Documented in the plan.
      featureFlagService.updateFlags(data.flags);
    } catch (err) {
      logger.error('Failed to save feature flags', err);
      setErrorMessage('Failed to save feature flags.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const grouped = groupByCategory(FEATURE_FLAG_REGISTRY);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Feature Flags
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enable or disable platform features at runtime. Changes propagate to
        all users on their next app load (up to ~60 seconds for backend
        cache to refresh across ECS tasks).
      </Typography>

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      )}

      <Stack spacing={3} divider={<Divider />}>
        {grouped.map(({ category, entries }) => (
          <Box key={category}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', mb: 1, fontWeight: 'bold' }}
            >
              {category}
            </Typography>
            <Stack spacing={2}>
              {entries.map((entry) => (
                <FormControlLabel
                  key={entry.key}
                  control={
                    <Switch
                      checked={Boolean(flags[entry.key])}
                      onChange={handleToggle(entry.key)}
                      inputProps={{ 'aria-label': entry.label }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{entry.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {entry.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mr: 0 }}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>

      <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {(updatedAt || updatedBy) && (
          <Typography variant="caption" color="text.secondary">
            {updatedAt && `Last updated: ${new Date(updatedAt).toLocaleString()}`}
            {updatedAt && updatedBy && ' · '}
            {updatedBy && `By: ${updatedBy}`}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default FeatureFlagsTab;
